import React from "react";
import { useState, useEffect } from "react";
import { Modal } from "@truevault/aquilo";
import { PrivacyCenterNoticeDto } from "../../../common/service/server/dto/PrivacyCenterDto";
import { Api } from "../../../common/service/Api";
import { useSnackBar } from "@truevault/aquilo";
import { formatNoticeDate } from "../../formatters/date";

/** Stable version identifier for diff/API. New: versionName; legacy: privacy-policy-v{N} */
const getVersionIdentifier = (notice: PrivacyCenterNoticeDto): string =>
  notice.versionName ?? `privacy-policy-v${notice.version}`;

export function CompareVersionsModal({
  orgId,
  privacyCenterId,
  publishedNotices,
  onClose,
  initialVersionName,
}: {
  orgId: string;
  privacyCenterId: string;
  publishedNotices: PrivacyCenterNoticeDto[];
  onClose: () => void;
  /** When provided, pre-fills the "Original" dropdown with this version and compares to Current (e.g. when opened from a row) */
  initialVersionName?: string;
}) {
  // Deduplicate by version identifier - first in list is Current (most recent)
  const uniqueByVersionId = React.useMemo(() => {
    const seen = new Set<string>();
    return publishedNotices.filter((notice) => {
      const vid = getVersionIdentifier(notice);
      if (seen.has(vid)) return false;
      seen.add(vid);
      return true;
    });
  }, [publishedNotices]);

  const currentVersionId = uniqueByVersionId[0] ? getVersionIdentifier(uniqueByVersionId[0]) : "";

  const [versionA, setVersionA] = useState<string>(initialVersionName ?? "");
  const [versionB, setVersionB] = useState<string>(currentVersionId);
  const [loading, setLoading] = useState(false);
  const [diffHtml, setDiffHtml] = useState<string | null>(null);
  const enqueueSnackbar = useSnackBar();

  const handleCompare = async () => {
    if (!versionA || !versionB) return;

    setLoading(true);
    setDiffHtml(null);

    try {
      const html = await Api.notice.generateRedlinesDiff(
        orgId,
        privacyCenterId,
        versionA,
        versionB,
      );
      // Inject overflow-x: hidden to hide horizontal scrollbar in iframe
      const htmlWithOverflowHidden = html.replace(
        /<\/style>/i,
        "    html, body { overflow-x: hidden; }\n  </style>",
      );
      setDiffHtml(htmlWithOverflowHidden);
    } catch (error: any) {
      console.error("Failed to generate diff:", error);
      const status = error?.response?.status;
      let data = error?.response?.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data) as { message?: string; error?: string };
        } catch {
          // Keep as string if not valid JSON
        }
      }
      const backendMessage =
        typeof data === "string"
          ? data
          : data?.message ??
            data?.error ??
            (data && typeof data === "object" ? JSON.stringify(data) : "");
      const statusLabel = status ? ` (${status})` : "";
      const versionContext = `Comparing "${versionA}" vs "${versionB}". `;
      const message =
        status === 404
          ? versionContext +
            (backendMessage || "One or both versions could not be found for comparison.")
          : status === 500
          ? versionContext +
            `Server error${statusLabel}: ${
              backendMessage || "Internal server error. Check backend logs."
            }`
          : status
          ? versionContext +
            `Request failed${statusLabel}: ${backendMessage || error?.message || "Unknown error"}`
          : versionContext + `Failed: ${error?.message || "Network or unknown error"}`;
      enqueueSnackbar(message, { variant: "error", persist: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (versionA && versionB) {
      void handleCompare();
    }
  }, [versionA, versionB]);

  const handleDownloadDocument = () => {
    if (!diffHtml) return;
    const blob = new Blob([diffHtml], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `privacy-policy-comparison-${versionA?.replace(
      /[/\\?*:|"]/g,
      "-",
    )}-vs-${versionB?.replace(/[/\\?*:|"]/g, "-")}.html`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const selectClassName =
    "rounded-md border border-neutral-300 pl-3 pr-8 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 bg-white min-w-[220px]";

  return (
    <Modal
      title={
        <div className="flex flex-wrap items-center gap-3 pr-8">
          <span className="text-lg font-medium text-gray-900">
            Privacy Policy Versions Comparison
          </span>
          <div className="flex items-center gap-2">
            <span className="text-sm text-neutral-600">Original</span>
            <select
              className={selectClassName}
              value={versionA}
              onChange={(e) => {
                setVersionA(e.target.value);
                setDiffHtml(null);
              }}
              disabled={loading}
            >
              <option value="">Select a version...</option>
              {uniqueByVersionId.map((notice, index) => {
                const vid = getVersionIdentifier(notice);
                return (
                  <option key={vid} value={vid} disabled={vid === versionB}>
                    {getNoticeLabel(notice)}
                    {index === 0 && " - Current"}
                  </option>
                );
              })}
            </select>
            <span className="text-sm text-neutral-500">vs.</span>
            <select
              className={selectClassName}
              value={versionB}
              onChange={(e) => {
                setVersionB(e.target.value);
                setDiffHtml(null);
              }}
              disabled={loading}
            >
              <option value="">Select a version...</option>
              {uniqueByVersionId.map((notice, index) => {
                const vid = getVersionIdentifier(notice);
                return (
                  <option key={vid} value={vid} disabled={vid === versionA}>
                    {getNoticeLabel(notice)}
                    {index === 0 && " - Current"}
                  </option>
                );
              })}
            </select>
          </div>
        </div>
      }
      showOverlay
      closeButton
      onClose={onClose}
      disableSmFullWidth
      panelStyle={{ maxWidth: "min(1920px, calc(100vw - 110px))" }}
      firstButton="Close"
      firstButtonColor="tertiary"
      onFirstButtonClick={onClose}
      secondButton="Download Document"
      secondButtonColor="primary"
      onSecondButtonClick={handleDownloadDocument}
      secondButtonDisabled={!diffHtml}
      alignButton="right"
      alignText="left"
      alignContent="center"
      contentContainerClasses="w-full"
      contentPadding="px-6 pt-[24px] pb-0"
      buttonPadding="px-6 pt-2 pb-[24px]"
    >
      <div className="flex flex-col items-center justify-center min-h-[60vh] w-full">
        {diffHtml ? (
          <div
            className="w-full min-h-[60vh] overflow-x-hidden overflow-y-auto bg-neutral-100"
            style={{ marginRight: "-1rem" }}
          >
            <div
              className="border border-neutral-200 rounded-lg shadow-md bg-white overflow-hidden"
              style={{ margin: "0 auto", width: "100%", maxWidth: "min(100%, 816px)" }}
            >
              <iframe
                srcDoc={diffHtml}
                className="w-full min-h-[60vh] border-0"
                title="Privacy Policy Diff"
                sandbox="allow-same-origin"
              />
            </div>
          </div>
        ) : (
          <div className="flex w-full flex-col items-center justify-center gap-6">
            <img
              src="/assets/images/compare-loading.png"
              alt=""
              className="max-w-[944px] max-h-[684px] w-full h-auto object-contain"
            />
            <p className="text-sm text-neutral-500">
              {loading ? "Generating comparison..." : "Select versions above to compare"}
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
}

/**
 * Returns a human-readable label from createdAt (when published), e.g. "Feb 18, 2026, 2:30 PM".
 * Uses browser locale/timezone.
 */
function getNoticeLabel(notice: PrivacyCenterNoticeDto): string {
  const date = new Date(notice.createdAt);
  if (!isNaN(date.getTime())) {
    return formatNoticeDate(date);
  }
  return notice.versionName || `v${notice.version}`;
}
